/** Small numeric helpers shared across the solver and the renderer. */

export const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v)

export const lerp = (a: number, b: number, t: number) => a + (b - a) * t

/** Mean of a slice, guarding against the empty case. */
export function mean(xs: ArrayLike<number>): number {
  if (xs.length === 0) return 0
  let s = 0
  for (let i = 0; i < xs.length; i++) s += xs[i]
  return s / xs.length
}

/** Population standard deviation. */
export function stdev(xs: ArrayLike<number>): number {
  if (xs.length === 0) return 0
  const m = mean(xs)
  let s = 0
  for (let i = 0; i < xs.length; i++) {
    const d = xs[i] - m
    s += d * d
  }
  return Math.sqrt(s / xs.length)
}

export function minMax(xs: ArrayLike<number>): { min: number; max: number } {
  let min = Infinity
  let max = -Infinity
  for (let i = 0; i < xs.length; i++) {
    if (xs[i] < min) min = xs[i]
    if (xs[i] > max) max = xs[i]
  }
  if (min === Infinity) return { min: 0, max: 0 }
  return { min, max }
}

/**
 * Non-uniformity as the semiconductor industry usually quotes it on a thickness
 * or rate map: half the full range over the mean, in percent. Lower is better.
 */
export function nonUniformityPct(xs: ArrayLike<number>): number {
  const m = mean(xs)
  if (m === 0) return 0
  const { min, max } = minMax(xs)
  return (100 * (max - min)) / (2 * m)
}

/** 1-sigma non-uniformity in percent, the other common convention. */
export function sigmaPct(xs: ArrayLike<number>): number {
  const m = mean(xs)
  if (m === 0) return 0
  return (100 * stdev(xs)) / m
}

export const toFixed = (v: number, d = 2) => (Number.isFinite(v) ? v.toFixed(d) : '--')

/**
 * Standard normal cumulative distribution.
 *
 * Abramowitz and Stegun 7.1.26, good to about 1.5e-7. Lives here because two
 * unrelated tools need it for the same reason: a measurement, or a bond, is
 * a distribution meeting a limit, and the answer is always the area on the
 * wrong side of that limit.
 */
export function normalCdf(z: number): number {
  const t = 1 / (1 + 0.2316419 * Math.abs(z))
  const d = 0.3989422804014327 * Math.exp((-z * z) / 2)
  const p =
    d *
    t *
    (0.31938153 +
      t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))))
  return z >= 0 ? 1 - p : p
}

/**
 * `toFixed` without the minus sign on a zero.
 *
 * A difference that lands a hair under zero formats as "-0.0", which reads as
 * a measurement ("the gap opens minus nothing") rather than as none. Anything
 * that rounds to zero at this precision is printed as zero.
 */
export function fixed(v: number, digits: number): string {
  const s = v.toFixed(digits)
  return /^-0(\.0*)?$/.test(s) ? s.slice(1) : s
}
